/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useEffect, useState } from 'react';
import {
  IAny,
  IRequestContext
} from '../../DashboardKPIWidgets/IEnergyManagementProps';
import useConnectorAndDynamicProps from '../../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import {
  convertUtilityProvidersToOptions,
  debounce,
  isObjectEmpty,
  SpecialCharacter
} from '../../shared/Utils/Utils';
import descriptors from '../../../locales/en-US/descriptors';
import { useTranslation } from '@scuf/localization';
import { IUtilityProviders } from '../IUtilityService';
import { Search, Icon, Tooltip } from '@scuf/common';
import { IResult } from '@scuf/common/dist/components/Search/ISearchProps';
import { MAGIC_VALUE, UtilityType } from '../../shared/Utils/Constants';
import { isLoadingCheck } from '../../shared/Utils/UtilityViewDetails';
import { connectorConfigCheck } from '../ConnectTab/ConnectTabUtils';
import { v4 as uuid4 } from 'uuid';

interface IChooseServiceTab {
  bearerToken?: string;
  requestContext: IRequestContext;
  SetSelectedUtilityProvider: Function;
  SelectedUtilityProvider: IUtilityProviders;
  utilityTyp?: string;
  setIsSpecialCharacter: IAny;
  isSpecialCharacter: boolean;
}
interface IUtilityProviderRes {
  data: IUtilityProviders[];
}
function UpdatedChooseServiceTab(props: IChooseServiceTab) {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const {
    bearerToken,
    SetSelectedUtilityProvider,
    SelectedUtilityProvider
  } = props;
  let { requestContext } = props;
  const [SearchKeyword, setSearchKeyword] = useState('');
  const [displaySelected, setDisplaySelected] = useState('');

  const connectorConfig: IAny = {
    name: connectorConfigCheck(SearchKeyword),
    options: {
      includeHbsmProviders: props.utilityTyp === 'Manual' ? 'true' : 'false'
    }
  };
  requestContext = {
    ...requestContext,
    context: {
      ...requestContext.context,
      searchKeyword: SearchKeyword
    }
  };
  let [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig,
    requestContext,
    bearerToken
  });
  const minimumSearchCharacter = 3;
  const [results, setResults] = useState<IResult[]>([]);

  useEffect(() => {
    props.utilityTyp === 'Automated' &&
      SetSelectedUtilityProvider({} as IUtilityProviders);
    setResults([]);
    if (SearchKeyword.length >= minimumSearchCharacter) {
      dataConnector && dataConnector.actions && dataConnector.actions.getAll();
    }
  }, [SearchKeyword]);

  useEffect(() => {
    isObjectEmpty(SelectedUtilityProvider) && setDisplaySelected('');
  }, [SelectedUtilityProvider]);

  const { all, loading, error, status } = dataConnector.data;
  const UtilityProviderResponse: IUtilityProviderRes = (all as unknown) as IUtilityProviderRes;
  let isDataAvailable: boolean;
  if (UtilityProviderResponse) isDataAvailable = true;
  else isDataAvailable = false;
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [, setStatusCode] = useState<number | null>(status);
  const [, setErrorInfo] = useState(error);
  const [, setData] = useState({});

  const saveDataForManual = () => {
    if (SearchKeyword && props.utilityTyp === 'Manual') {
      props.setIsSpecialCharacter(false);
      let isPresent = false;
      let ind: IAny = '';
      if (
        UtilityProviderResponse &&
        UtilityProviderResponse.data &&
        UtilityProviderResponse.data.length
      ) {
        UtilityProviderResponse?.data.forEach((res: IAny, index: number) => {
          if (res.utilityProviderName === SearchKeyword) {
            isPresent = true;
            ind = index;
          }
        });
      }
      let provId = uuid4();
      if (isPresent) {
        provId = UtilityProviderResponse?.data[ind].utilityProviderId;
      }

      setDisplaySelected('');
      SetSelectedUtilityProvider({
        utilityProviderName: SearchKeyword,
        utilityProviderId: provId
      });
    }
  };

  useEffect(() => {
    setIsLoading(loading);

    if (!isDataAvailable && SearchKeyword && props.utilityTyp === 'Manual') {
      props.setIsSpecialCharacter(false);
      SetSelectedUtilityProvider({
        utilityProviderName: SearchKeyword,
        utilityProviderId: uuid4()
      });
    }
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        setData(UtilityProviderResponse);

        saveDataForManual();
        let UtilityProviders = convertUtilityProvidersToOptions(
          UtilityProviderResponse?.data
        );
        let searchresults = UtilityProviders.map((provider) => {
          return {
            title: provider.text,
            value: provider.value
          };
        });
        let filteredResults = searchresults.filter((searchEle) => {
          return searchEle.title
            .toLowerCase()
            .includes(SearchKeyword.toLowerCase());
        });
        setResults(filteredResults);
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading]);

  const SearchProvider = async (value: string) => {
    if (
      value.length <= MAGIC_VALUE.HUNDRED &&
      (props.utilityTyp === 'Manual' || !SpecialCharacter(value))
    ) {
      setSearchKeyword(value.trim());
    }
  };

  const debounceTimeLimit = 500;
  const debouncedSearch = debounce(SearchProvider, debounceTimeLimit);

  return (
    <div className="choose-updated-service-tab">
      <p className="choose-utility-note">
        {props.utilityTyp === 'Automated'
          ? t(d.CHOOSEUTILITYTYPE)
          : t(d.CHOOSEUTILITYTYPEMANUAL)}
      </p>
      <label className="utility-provider-label">
        {t(d.UTILITYPROVIDERLABEL)}*
        <div className="tooltip-utility">
          <Tooltip
            element={<Icon root="common" name="badge-info" size="small" />}
            event="hover"
            header={''}
            content={t(d.UTILITYPROVIDERTOOLTIPTXT)}
            position="top right"
            size="small"
          />
        </div>
      </label>
      <div className="select-loader-wrapper">
        <Search
          placeholder={t(d.SEARCHPROVIDER)}
          noResultsMessage={isLoadingCheck(isLoading, t)}
          results={results}
          value={SelectedUtilityProvider.utilityProviderName}
          onSearchChange={(value) => {
            debouncedSearch(value);
          }}
          onResultSelect={(value) => {
            setDisplaySelected(value.title);
            SetSelectedUtilityProvider(JSON.parse(value.value));
          }}
        />
        {props.isSpecialCharacter && (
          <span className="special-character-error">
            Provider name cannot contain special character
          </span>
        )}
      </div>
      {props.utilityTyp === 'Automated' && (
        <p className="support-mssg">{t(d.CONTACTSUPPORT)}</p>
      )}
    </div>
  );
}

export default UpdatedChooseServiceTab;
